/**
 * Shared / rental vehicle type preferences for the itinerary settings MiniModal.
 *
 * Renders the vehicle form-factor selector that drives `rentalVehicleTypes` on
 * itinerary search requests.
 */

import React, { useCallback, useMemo } from "react";
import { rentalVehiclesPrefsSlice, usePersistedSlice } from "../../../../../store/persistence";
import {
    mapOptionIdsToRentalVehicleTypes,
    mapRentalVehicleTypesToOptionIds,
} from "../../../../../tools/rentalVehiclePrefs.tools";
import ImageOptionSelector, { type ImageSelectorOption } from "../../../../MiniModal/ImageOptionSelector";
import MiniModalSection from "../../../../MiniModal/MiniModalSection";

const RENTAL_VEHICLES_ICON_BASE = "/assets/icon/rental-vehicles_prefs";

/** Vehicle type options for the shared-vehicles tab selector. */
const RENTAL_VEHICLE_SELECTOR_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "bicycle",
        "label":    "Vélo",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/bicycle.webp`,
        "imgCover": false,
    },
    {
        "id":       "cargo_bicycle",
        "label":    "Vélo cargo",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/bicycleCargo.webp`,
        "imgCover": false,
    },
    {
        "id":       "moped",
        "label":    "Moto / Scooter",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/moped.webp`,
        "imgCover": false,
    },
    {
        "id":       "scooter_standing",
        "label":    "Trotinette",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/scooterStanding.webp`,
        "imgCover": false,
    },
    {
        "id":       "scooter_seated",
        "label":    "Trottinette assise",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/scooterSeated.webp`,
        "imgCover": false,
    },
    {
        "id":       "car",
        "label":    "Voiture",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/carSharing.webp`,
        "imgCover": false,
    },
    {
        "id":       "other",
        "label":    "Autre",
        "imageSrc": `${ RENTAL_VEHICLES_ICON_BASE }/other.webp`,
        "imgCover": false,
    },
];

/** Props for {@link RentalVehiclesPrefsModalContent}. */
export interface RentalVehiclesPrefsModalContentProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;
}

/**
 * Shared-vehicle type selector panel for the itinerary settings modal.
 *
 * @param props Component props. See {@link RentalVehiclesPrefsModalContentProps}.
 * @returns Rental vehicle preference sections.
 */
const RentalVehiclesPrefsModalContent: React.FC<RentalVehiclesPrefsModalContentProps> = ( { isDarkUi } ) => {
    const [ rentalVehiclesPrefs, setRentalVehiclesPrefs ] = usePersistedSlice( rentalVehiclesPrefsSlice );

    const selectedOptionIds = useMemo( (): readonly string[] => {
        return mapRentalVehicleTypesToOptionIds( rentalVehiclesPrefs.enabledTypes );
    }, [ rentalVehiclesPrefs.enabledTypes ] );

    /**
     * Persist the updated rental vehicle type selection.
     *
     * @param nextValue Selected checkbox option ids.
     */
    const handleVehicleTypesChange = useCallback( ( nextValue: readonly string[] ): void => {
        setRentalVehiclesPrefs( { "enabledTypes": mapOptionIdsToRentalVehicleTypes( nextValue ) } );
    }, [ setRentalVehiclesPrefs ] );

    return (
        <MiniModalSection
            isDarkUi={isDarkUi}
            title="Types de véhicules"
        >
            <ImageOptionSelector
                columns={3}
                isDarkUi={isDarkUi}
                onChange={handleVehicleTypesChange}
                options={RENTAL_VEHICLE_SELECTOR_OPTIONS}
                selectionType="checkbox"
                value={selectedOptionIds}
            />
        </MiniModalSection>
    );
};

export default RentalVehiclesPrefsModalContent;
